import clsx from 'clsx'
import {FC, FocusEventHandler, ReactNode, useState} from 'react'
import {
  ActionMeta,
  GroupBase,
  OptionsOrGroups,
  components,
  Options,
  InputActionMeta,
} from 'react-select'
import AsyncSelect from 'react-select/async'
import Creatable from 'react-select/creatable'

type Props = {
  options?: OptionsOrGroups<unknown, GroupBase<unknown>> | undefined
  onChange?: (newValue: unknown, actionMeta: ActionMeta<unknown>) => void
  placeholder?: ReactNode
  isMulti?: any
  isCreatable?: boolean
  onCreateOption?: (inputValue: string) => void | undefined
  value?: any
  isOptionDisabled?: (option: any, selectValue: Options<any>) => boolean
  isClearable?: boolean
  isDisabled?: boolean
  loadOptions?: any
  onBlur?: FocusEventHandler<HTMLInputElement>
  isValid?: boolean
  errorMessage?: string
  useBody?: boolean
}

const DropdownAsyncSelect: FC<Props> = ({
  options,
  onChange,
  placeholder,
  isMulti,
  isCreatable = false,
  onCreateOption,
  value,
  isOptionDisabled,
  isClearable,
  isDisabled,
  loadOptions,
  onBlur,
  isValid,
  errorMessage,
  useBody = false,
}) => {
  if (isCreatable) {
    return (
      <Creatable
        components={{
          IndicatorSeparator: null,
          Control: (props) => (
            <components.Control
              {...props}
              className={clsx(props.className, 'custom-dropdown-control')}
            />
          ),
          Input: (props) => (
            <components.Input
              {...props}
              className={clsx(props.className, 'custom-dropdown-input')}
            />
          ),
        }}
        onBlur={onBlur}
        isMulti={isMulti}
        options={options}
        onChange={onChange}
        onCreateOption={onCreateOption}
        placeholder={<span className='custom-dropdown-placeholder fw-semibold'>{placeholder}</span>}
        value={value}
        isOptionDisabled={isOptionDisabled}
        isClearable={isClearable}
        isDisabled={isDisabled}
      />
    )
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>()

  const onInputChange = (inputValue: string, {action, prevInputValue}: InputActionMeta) => {
    if (action === 'input-change') return inputValue
    if (action === 'menu-close') {
      if (prevInputValue) setMenuIsOpen(true)
      else setMenuIsOpen(undefined)
    }
    return prevInputValue
  }

  return (
    <>
      <AsyncSelect
        components={{
          IndicatorSeparator: null,
          Control: (props) => (
            <components.Control
              {...props}
              className={clsx(props.className, 'custom-dropdown-control')}
            />
          ),
          Input: (props) => (
            <components.Input
              {...props}
              className={clsx(props.className, 'custom-dropdown-input')}
            />
          ),
        }}
        styles={{
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 3,
          }),
        }}
        isMulti={isMulti}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={<span className='custom-dropdown-placeholder fw-semibold'>{placeholder}</span>}
        value={value}
        isSearchable
        cacheOptions
        defaultOptions
        isClearable={isClearable}
        loadOptions={loadOptions}
        isDisabled={isDisabled}
        onInputChange={onInputChange}
        menuIsOpen={menuIsOpen}
        menuPortalTarget={useBody ? document.body : null}
      />
      {!isValid && errorMessage && <span className='text-danger mt-2'>{errorMessage}</span>}
    </>
  )
}

export {DropdownAsyncSelect}
